/**
 * SC-257: pure decision helpers for remote-audio playback recovery.
 *
 * When the LiveKit room uses `webAudioMix: true`, all remote voice is mixed through a
 * single Web Audio AudioContext. Browsers suspend that context while the tab is
 * backgrounded (notably Safari/iOS) and block audio elements created while hidden under
 * autoplay policy, which makes remote participants inaudible until playback is resumed.
 *
 * These helpers are side-effect-free so the logic that drives `room.startAudio()` and the
 * click-to-resume affordance can be unit-tested without a live LiveKit Room.
 */

export interface AudioResumeConditions {
  /** Room is in the Connected state. */
  connected: boolean
  /** LiveKit `Room.canPlaybackAudio` — true when the audio context is actively playing. */
  canPlaybackAudio: boolean
}

/**
 * Whether to attempt `room.startAudio()`.
 * Only when the room is connected AND playback is currently blocked/suspended — otherwise
 * it is a no-op (disconnected) or unnecessary (already playing).
 */
export function shouldAttemptAudioResume({ connected, canPlaybackAudio }: AudioResumeConditions): boolean {
  return connected && !canPlaybackAudio
}

/** How long a forced resume on a room that reports itself playing is not repeated. */
export const FORCED_AUDIO_RESUME_COOLDOWN_MS = 5000

export interface AudioResumeAttemptInput extends AudioResumeConditions {
  /** The request came from a real user gesture and may skip the `canPlaybackAudio` gate. */
  force: boolean
  /** When the last forced attempt ran, or `null` if none has. */
  lastForcedAt: number | null
  now: number
}

export interface AudioResumeAttempt {
  attempt: boolean
  /** Whether this attempt may name what resumed playback on the next "resumed" analytics event. */
  recordSource: boolean
}

/**
 * Whether to call `room.startAudio()` for this request, and whether to record its source.
 *
 * `force` exists because `canPlaybackAudio` cannot see a suspended `webAudioMix` AudioContext (SC-257 ×
 * SC-209, `livekitAudioResumeRequest.ts`), so a request from a real gesture runs even on a room that reports
 * itself playing. But a Document PiP window sends one for every pointerdown, keydown and touchstart, and
 * each `startAudio()` rebuilds every remote track's Web Audio nodes. So on a room that reports itself
 * playing, one forced attempt per cooldown is enough to wake a suspended context; a blocked room is always
 * attempted. Only an attempt on a blocked room records its source: the source labels whatever ended a block,
 * and a forced call on a healthy room ended nothing.
 */
export function decideAudioResumeAttempt(input: AudioResumeAttemptInput): AudioResumeAttempt {
  if (!input.connected) return { attempt: false, recordSource: false }
  if (shouldAttemptAudioResume(input)) return { attempt: true, recordSource: true }
  if (!input.force) return { attempt: false, recordSource: false }
  const coolingDown =
    input.lastForcedAt !== null && input.now - input.lastForcedAt < FORCED_AUDIO_RESUME_COOLDOWN_MS
  return { attempt: !coolingDown, recordSource: false }
}

export type AudioPlaybackTransition =
  | { blocked: true; event: 'blocked' }
  | { blocked: false; event: 'resumed' }
  | { blocked: boolean; event: null }

/**
 * Given the previously-known blocked state and the current `canPlaybackAudio`, decide the
 * new blocked state and whether this is a reportable edge.
 *
 * Only transitions emit an event so analytics/affordance updates don't fire on repeated
 * same-state notifications:
 *   - not-playing while not-yet-blocked  -> becomes blocked, event 'blocked'
 *   - playing while currently blocked     -> becomes unblocked, event 'resumed'
 *   - otherwise                           -> unchanged, event null
 */
export function decideAudioPlaybackTransition(
  prevBlocked: boolean,
  canPlaybackAudio: boolean
): AudioPlaybackTransition {
  if (!canPlaybackAudio && !prevBlocked) return { blocked: true, event: 'blocked' }
  if (canPlaybackAudio && prevBlocked) return { blocked: false, event: 'resumed' }
  return { blocked: prevBlocked, event: null }
}
